/**
 * Loading a screen in parallel instead of in a chain (docs/ux/plan.md, B7 and B8).
 *
 * A cold deep link used to wait for: the entry, the session refresh, the shell chunk, the
 * agency, the screen's layout chunk, its data, the page chunk, then the page's data. Now
 * main.tsx calls bootRoute() before the first render, which starts at once, side by side:
 *   - the session bootstrap (one call: refresh, branding, agency, sub-accounts, plan),
 *   - the shell chunk and the screen's own chunks,
 *   - the screen's first reads, as soon as the session is back (they wait for nothing else).
 * The screens' own requests then find those answers (ApiClient.prefetch) instead of asking.
 *
 * The same warm-up runs when the pointer or focus reaches a sidebar link (warmRouteSoon), so a
 * click finds the screen and its data ready.
 */
import type { Api } from './api';
import type { BootstrapHint } from './api/client';
import { track } from './quiet';
// The record-name context (214 B) in the first bundle: the shell provides it and detail screens
// set it (useRecordName), so otherwise it becomes a chunk of its own, one more request on every
// cold load before the first screen shows.
import './shell/record-name';

type BootRead = NonNullable<BootstrapHint['reads']>[number];

/**
 * The first reads of the most used screens, asked for in the bootstrap itself so they do not
 * wait for it (one round trip less). The same requests as their warm-up entries
 * (route-warmup.ts); test/route-prefetch.test.tsx checks both against the screens.
 */
export function bootReads(agencyId: string, rest: string, inSubaccount: boolean): BootRead[] {
  if (/^\/?$/.test(rest)) return [{ method: 'GET', path: `/agencies/${encodeURIComponent(agencyId)}/dashboard` }];
  if (!inSubaccount) return [];
  if (/^\/contacts\/?$/.test(rest)) {
    return [
      { method: 'POST', path: '/contacts/search', body: { filter: {}, limit: 25 } },
      { method: 'GET', path: '/contacts/tags' },
      { method: 'GET', path: '/contacts/smart-lists' },
    ];
  }
  if (/^\/pipelines\/?$/.test(rest)) return [{ method: 'GET', path: '/pipelines' }, { method: 'GET', path: '/opportunities/assignees' }];
  if (/^\/inbox(\/[^/]+)?\/?$/.test(rest)) return [{ method: 'GET', path: '/conversations', query: { status: 'open' } }];
  if (/^\/payments\/invoices\/?$/.test(rest)) return [{ method: 'GET', path: '/payments/invoices' }];
  return [];
}

/**
 * The most used screens' code, started by bootRoute itself rather than by the warm-up table
 * (which is its own chunk, so going through it costs a round trip on a cold load): the screen's
 * chunks and the endpoint table its first reads use. route-warmup.ts lists the same chunks for
 * hover warm-ups; importing a module twice fetches it once.
 */
const BOOT_CHUNKS: ReadonlyArray<{ match: RegExp; chunks: Array<() => Promise<unknown>> }> = [
  { match: /^\/?$/, chunks: [() => import('./pages/AgencyDashboard')] },
  { match: /^\/contacts\/?$/, chunks: [() => import('./pages/contacts/ContactsPage'), () => import('./api/contacts')] },
  { match: /^\/pipelines\/?$/, chunks: [() => import('./pages/pipelines/PipelinesLayout'), () => import('./pages/pipelines/PipelineBoardPage'), () => import('./api/pipelines')] },
  { match: /^\/inbox(\/[^/]+)?\/?$/, chunks: [() => import('./inbox/InboxPage'), () => import('./api/inbox')] },
  { match: /^\/payments\/invoices\/?$/, chunks: [() => import('./payments/routes'), () => import('./payments/PaymentsLayout'), () => import('./payments/InvoicesPage'), () => import('./api/payments')] },
  // The other tools' landing screens and the contact record: code only (their reads start when the screen renders)
  { match: /^\/contacts\/(?!import\/?$|settings\/?$)[^/]+\/?$/, chunks: [() => import('./pages/contacts/ContactDetailPage'), () => import('./api/contacts')] },
  // The calendar's endpoint table with its screen: its first read waited for it (WP4)
  { match: /^\/calendar\/?$/, chunks: [() => import('./pages/calendar/CalendarSection'), () => import('./api/booking')] },
  { match: /^\/automations\/?$/, chunks: [() => import('./automations/routes'), () => import('./automations/AutomationsLayout'), () => import('./automations/WorkflowListPage')] },
  { match: /^\/campaigns\/?$/, chunks: [() => import('./campaigns/routes'), () => import('./campaigns/CampaignsLayout'), () => import('./campaigns/CampaignListPage')] },
  { match: /^\/courses\/?$/, chunks: [() => import('./courses/CoursesRoutes'), () => import('./courses/CoursesLayout'), () => import('./courses/CoursesListPage')] },
  { match: /^\/social\/?$/, chunks: [() => import('./social/routes'), () => import('./social/SocialLayout'), () => import('./social/CalendarPage')] },
  { match: /^\/sites\/pages\/?$/, chunks: [() => import('./sites/routes'), () => import('./sites/SitesLayout'), () => import('./sites/PagesPage')] },
  { match: /^\/proposals\/?$/, chunks: [() => import('./proposals/routes'), () => import('./proposals/ProposalsLayout'), () => import('./proposals/DashboardPage')] },
  { match: /^\/phone\/?$/, chunks: [() => import('./phone/PhoneSection'), () => import('./phone/CallLogPage')] },
  { match: /^\/reputation\/?$/, chunks: [() => import('./reputation/ReputationRoutes'), () => import('./reputation/ReputationLayout'), () => import('./reputation/FeedPage')] },
  { match: /^\/integrations\/?$/, chunks: [() => import('./integrations/integrations-section')] },
];

/** Start a cold-loaded screen's code now (see BOOT_CHUNKS) */
export function bootChunks(rest: string): Array<Promise<unknown>> {
  const entry = BOOT_CHUNKS.find((r) => r.match.test(rest));
  return entry ? entry.chunks.map((load) => track(load()).catch(report(`a screen for ${rest || '/'}`))) : [];
}

const report = (what: string) => (error: unknown) => console.warn(`[prefetch] ${what} could not be loaded early; it loads again when needed`, error);

const AGENCY_PATH = /^\/agencies\/([^/]+)(\/.*)?$/;

/** Split an app path into the agency and the screen below it */
export function agencyPath(pathname: string): { agencyId: string; rest: string } | null {
  const match = AGENCY_PATH.exec(pathname);
  return match ? { agencyId: decodeURIComponent(match[1]!), rest: match[2] ?? '' } : null;
}

/**
 * Before the first render (main.tsx): with a stored session, resume it with one bootstrap
 * call and start the shell, the screen's chunks and its data in parallel.
 */
export function bootRoute(api: Api, location: { pathname: string; search: string }): void {
  if (!api.client.needsRestore) return;
  const inAgency = agencyPath(location.pathname);
  // AuthProvider joins this same refresh (it is single-flight) and reports a failure
  const inSubaccount = api.client.getState().scope.kind === 'subaccount';
  const hint: BootstrapHint = inAgency ? { agencyId: inAgency.agencyId, reads: bootReads(inAgency.agencyId, inAgency.rest, inSubaccount) } : {};
  const resumed = api.client.resume(hint);
  resumed.catch(report('the session'));
  // The board needs the pipeline list, which rides in the bootstrap: ask for the board the
  // moment the list is back, instead of after the board screen's code has downloaded
  if (inAgency && inSubaccount && /^\/pipelines\/?$/.test(inAgency.rest)) {
    const wanted = new URLSearchParams(location.search).get('pipeline');
    resumed
      .then(() => api.client.peekEarly<Array<{ id: string }>>('GET', '/pipelines'))
      .then((list) => {
        const chosen = list?.find((p) => p.id === wanted) ?? list?.[0];
        if (chosen) return api.client.prefetch('GET', `/pipelines/${encodeURIComponent(chosen.id)}/board`, { query: { status: 'open' } });
        return undefined;
      })
      .catch(report('the pipeline board'));
  }
  if (inAgency || location.pathname === '/agencies' || location.pathname === '/security') track(import('./shell/routes')).catch(report('the app shell'));
  // A most-used screen: its code starts here and its first reads ride in the bootstrap, so the
  // warm-up table would only repeat them (after the screen has taken the answers: a second fetch)
  if (inAgency && bootChunks(inAgency.rest).length === 0) warmRouteSoon(api, inAgency.agencyId, inAgency.rest, location.search);
}

/** Load the warm-up table (its own chunk) and warm one agency screen (`chunksOnly`: no data) */
export function warmRouteSoon(api: Api, agencyId: string, rest: string, search = '', chunksOnly = false): void {
  import('./route-warmup').then(
    (m) => void m.warmRoute(api, agencyId, rest, search, chunksOnly),
    (error: unknown) => console.warn('[prefetch] the warm-up table could not be loaded; screens load on their own', error)
  );
}
